import { getLogger } from '@packages/core/logging';

import type { AnswerAssetEntity } from '../entities';
import type { CreateAnswerAssetBody } from './types';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toAnswerAssetEntity } from '../helpers/mappers';

const logger = getLogger('techline');

/** Parameters for creating and uploading worksheet answer assets. */
export type CreateAndUploadAnswerAssetsParams = {
    /** Asset metadata entries to create records for. */
    assets: CreateAnswerAssetBody[];

    /** The actual files to upload, ordered to match the assets array. */
    files: File[];

    /** The techline case status string (used for categorization). */
    techlineCaseStatus: string;
};

/**
 * Creates answer asset records and uploads the corresponding files to presigned URLs.
 * Each asset in the request maps 1:1 to a file; after creation, files are uploaded in parallel.
 */
export class CreateAndUploadAnswerAssets extends StsBackendOperation {
    /**
     * Create asset records via the API and upload each file to its presigned URL.
     *
     * @param params - the asset metadata, files, and case status
     * @throws {Error} when the API response indicates failure
     * @returns the created answer asset entities
     */
    async exec(params: CreateAndUploadAnswerAssetsParams): Promise<AnswerAssetEntity[]> {
        // Create asset records via the API
        const response = await this.client.createAnswerAssets({
            assets: params.assets,
            techlinecasestatus: params.techlineCaseStatus,
        });

        if (!response.success) {
            throw new Error('Failed to create answer assets');
        }

        // Upload each file to its corresponding presigned URL in parallel
        await Promise.all(
            response.data.results.assets.map((asset, idx) =>
                uploadAnswerAssetToPresignedUrl(
                    asset.presignedUpload.url,
                    asset.presignedUpload.httpHeaders,
                    params.files[idx]
                )
            )
        );

        // Map response assets to domain entities
        return response.data.results.assets.map((item) => toAnswerAssetEntity(item.asset));
    }
}

/**
 * Upload an answer asset file to a presigned URL using the Fetch API.
 * This is a simple PUT request; no progress tracking is needed for worksheet answer assets.
 *
 * @param url - the presigned URL to upload to
 * @param headers - the HTTP headers required for the presigned upload
 * @param body - the file content to upload
 * @returns the fetch Response
 */
function uploadAnswerAssetToPresignedUrl(
    url: string,
    headers: Record<string, string>,
    body: File | Blob
): Promise<Response> {
    return fetch(url, {
        method: 'PUT',
        body,
        headers: { ...headers },
    }).then((response) => {
        if (!response.ok) {
            logger.error(`Failed to upload an answer asset to S3 [${response.status}]`, { fileSize: body.size });
        }
        return response;
    });
}
